import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RaceSectionProps {
    title: string;
    icon: ReactNode;
    children: ReactNode;
    isEmpty: boolean;
    emptyMessage: string;
    totalCount: number;
    displayedCount: number;
    onSeeAll?: () => void;
    seeAllLabel?: string;
    className?: string;
    /** Left accent border color, e.g. "border-l-emerald-400" */
    accentBorder?: string;
}

export function RaceSection({
    title,
    icon,
    children,
    isEmpty,
    emptyMessage,
    totalCount,
    displayedCount,
    onSeeAll,
    seeAllLabel,
    className,
    accentBorder,
}: RaceSectionProps) {
    return (
        <section className={cn(
            "space-y-3",
            accentBorder && "border-l-3 pl-4 rounded-sm",
            accentBorder,
            className,
        )}>
            <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    {icon}
                    {title}
                </h2>
                {!isEmpty && totalCount > displayedCount && onSeeAll && (
                    <button
                        onClick={onSeeAll}
                        className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                    >
                        {seeAllLabel}
                        <ArrowRight size={14} />
                    </button>
                )}
            </div>

            {isEmpty ? (
                <div className="py-8 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                    <p className="text-sm text-slate-400">{emptyMessage}</p>
                </div>
            ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {children}
                </div>
            )}
        </section>
    );
}
